import React, { useState, useEffect } from 'react';
import { LogOut } from 'lucide-react';
import { NavState, HQPanelId, OfficePanelId, OfficeId } from '../lib/types';
import { OFFICES, getOffice } from '../lib/config';
import { useSettings } from '../hooks/useSettings';
import { useAuth } from '../context/AuthContext';

import Dock from './Dock';
import OfficeDock from './OfficeDock';
import Panel from './Panel';

import ChatPanel from './chat/ChatPanel';
import OfficesGrid from './offices/OfficesGrid';
import OfficeScreen from './offices/OfficeScreen';
import KanbanBoard from './kanban/KanbanBoard';
import GanttChart from './gantt/GanttChart';
import CalendarView from './calendar/CalendarView';
import ReportsPanel from './reports/ReportsPanel';
import RegistryPanel from './registry/RegistryPanel';
import NotificationsPanel from './notifications/NotificationsPanel';
import SettingsPanel from './settings/SettingsPanel';
import UserManagementPanel from './admin/UserManagementPanel';
import AgentWorkstation from './agents/AgentWorkstation';
import { apiListNotifications } from '../lib/api';

const WALLPAPER =
  'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&q=80&w=2564';

/**
 * Desktop — top-level navigation container for KAELO HQ.
 * Manages NavState and renders the appropriate panel + dock.
 * No dragging or resizing — each panel is full-screen.
 */
export default function Desktop() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const [nav, setNav] = useState<NavState>({ view: 'hq', panel: 'offices' });
  const [pendingNotifCount, setPendingNotifCount] = useState(0);

  // Poll pending notification count
  useEffect(() => {
    const fetchCount = () => {
      apiListNotifications()
        .then((notifs) => setPendingNotifCount(notifs.filter((n) => n.status === 'pending').length))
        .catch(() => {});
    };
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => clearInterval(id);
  }, []);

  const goToOffice = (officeId: OfficeId) =>
    setNav({ view: 'office', officeId, panel: 'chat' });

  const goToAgent = (agentId: string, officeId?: OfficeId) =>
    setNav({ view: 'agent', agentId, officeId });

  const goToHQ = (panel: HQPanelId = 'offices') =>
    setNav({ view: 'hq', panel });

  // ── Compute tint for office backgrounds ─────────────────────────────────────
  const officeTint =
    nav.view === 'office'
      ? `${getOffice(nav.officeId)?.color || '#ffffff'}18`
      : undefined;

  const panelTitle = getPanelTitle(nav);

  return (
    <div
      className="relative w-full h-screen overflow-hidden"
      style={{
        backgroundImage: `url(${WALLPAPER})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      data-testid="desktop"
    >
      {/* Subtle office tint overlay */}
      {officeTint && (
        <div
          className="absolute inset-0 pointer-events-none z-0"
          style={{ backgroundColor: officeTint }}
        />
      )}

      {/* Top-right user + sign-out (desktop + mobile) */}
      <div className="absolute top-2 right-2 md:top-2 md:right-3 z-30 flex items-center gap-2 select-none">
        <span className="hidden sm:inline text-white/85 text-xs font-medium drop-shadow-sm">
          {user?.name}
        </span>
        <button
          onClick={logout}
          className="flex items-center gap-1.5 text-white/80 text-xs font-medium bg-black/25 hover:bg-black/40 active:bg-black/50 transition-colors px-2.5 py-1 rounded-lg"
          data-testid="logout-button"
        >
          <LogOut size={12} />
          <span className="hidden md:inline">Sign Out</span>
        </button>
      </div>

      {/* Panel area — leaves room for floating dock at bottom */}
      <div className="absolute top-10 left-2 right-2 bottom-20 md:top-4 md:left-4 md:right-4 md:bottom-24 z-20">
        {nav.view === 'agent' ? (
          <AgentWorkstation
            agentId={nav.agentId}
            officeId={nav.officeId}
            settings={settings}
            onClose={() =>
              nav.officeId
                ? setNav({ view: 'office', officeId: nav.officeId!, panel: 'team' })
                : goToHQ('offices')
            }
          />
        ) : nav.view === 'office' ? (
          <OfficeScreen
            officeId={nav.officeId}
            activePanel={nav.panel}
            settings={settings}
            onNavigate={(p) => setNav({ view: 'office', officeId: nav.officeId, panel: p })}
            onOpenAgent={(agentId) => goToAgent(agentId, nav.officeId)}
            onBack={() => goToHQ('offices')}
          />
        ) : (
          <Panel
            title={panelTitle}
            onClose={() => {}}
            data-testid={`panel-${nav.panel}`}
          >
            <HQPanelContent
              panel={nav.panel}
              settings={settings}
              onOpenOffice={goToOffice}
              onOpenAgent={goToAgent}
            />
          </Panel>
        )}
      </div>

      {/* Dock */}
      {(nav.view === 'hq') && (
        <Dock
          activePanel={nav.panel}
          onSelect={(p) => setNav({ view: 'hq', panel: p })}
          notificationCount={pendingNotifCount}
        />
      )}
      {nav.view === 'office' && (() => {
        const office = getOffice(nav.officeId);
        if (!office) return null;
        return (
          <OfficeDock
            office={office}
            activePanel={nav.panel}
            onSelect={(p) => setNav({ view: 'office', officeId: nav.officeId, panel: p })}
            onBack={() => goToHQ('offices')}
          />
        );
      })()}
      {nav.view === 'agent' && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 md:bottom-4">
          <button
            onClick={() =>
              nav.officeId
                ? setNav({ view: 'office', officeId: nav.officeId!, panel: 'team' })
                : goToHQ('offices')
            }
            className="flex items-center gap-2 px-4 py-2 rounded-2xl bg-white/90 border border-gray-200 shadow-sm text-sm font-medium text-gray-700 hover:bg-white transition-colors"
            data-testid="agent-workstation-back"
          >
            ← Back
          </button>
        </div>
      )}
    </div>
  );
}

// ── HQ Panel Content Router ───────────────────────────────────────────────────

interface HQPanelContentProps {
  panel: HQPanelId;
  settings: any;
  onOpenOffice: (id: OfficeId) => void;
  onOpenAgent: (agentId: string, officeId?: OfficeId) => void;
}

function HQPanelContent({ panel, settings, onOpenOffice, onOpenAgent }: HQPanelContentProps) {
  switch (panel) {
    case 'chat':
      return (
        <ChatPanel
          conversationId="hq"
          agentName="KAELO"
          webhookUrl={settings.hq?.chatWebhookUrl}
          pollUrl={settings.hq?.chatPollUrl}
        />
      );
    case 'offices':
      return <OfficesGrid onSelectOffice={onOpenOffice} />;
    case 'kanban':
      return <KanbanBoard />;
    case 'gantt':
      return <GanttChart />;
    case 'calendar':
      return <CalendarView />;
    case 'reports':
      return <ReportsPanel settings={settings.reports} />;
    case 'registry':
      return <RegistryPanel />;
    case 'notifications':
      return <NotificationsPanel />;
    case 'settings':
      return <SettingsPanel />;
    case 'users':
      return <UserManagementPanel />;
    default:
      return null;
  }
}

// ── Title helper ──────────────────────────────────────────────────────────────

function getPanelTitle(nav: NavState): string {
  if (nav.view === 'hq') {
    const map: Record<HQPanelId, string> = {
      chat: 'KAELO Chat',
      offices: 'KAELO HQ — Offices',
      kanban: 'HQ Kanban',
      gantt: 'HQ Timeline',
      calendar: 'Calendar',
      reports: 'Reports',
      registry: 'Implementation Registry',
      notifications: 'Notifications',
      settings: 'Settings',
      users: 'User Management',
    };
    return map[nav.panel];
  }
  return 'KAELO';
}
